import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Plus, Search, ClipboardList, Trash2, X, Check,
  BookOpen, Mail, FileDown, FileSpreadsheet, ArrowRightCircle, Save,
} from 'lucide-react';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { QuoteBulkDiscountBar } from '@/components/QuoteBulkDiscountBar';
import { QuoteAddAllPanel } from '@/components/QuoteAddAllPanel';

const FALLBACK_IMG = '/ije-fallback.svg';
const thumbImg = (u) => {
  if (!u) return FALLBACK_IMG;
  if (u.startsWith('/api/images/')) {
    const sep = u.includes('?') ? '&' : '?';
    return `${API_BASE}${u}${sep}thumb=1`;
  }
  if (u.startsWith('/api/')) return `${API_BASE}${u}`;
  return u;
};
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Build the inline line_item used for live totals + save payload.
 */
const emptyLine = (p) => ({
  sku: p.sku,
  name: p.name,
  brand: p.brand,
  images: p.images || [],
  wholesale_price: Number(p.wholesale_price || 0),
  msrp: Number(p.msrp || 0),
  qty: p.min_order_qty || 1,
  discount_pct: 0,
  override_price: '',
  line_note: '',
});

/**
 * computeLineTotal — override wins over discount_pct, matches backend math.
 */
const computeLine = (ln) => {
  const wholesale = Number(ln.wholesale_price || 0);
  const qty = Math.max(1, parseInt(ln.qty, 10) || 1);
  const override = ln.override_price !== '' && ln.override_price != null ? Number(ln.override_price) : null;
  let unit = wholesale;
  let effDisc = 0;
  if (override != null && override > 0) {
    unit = round2(override);
    effDisc = wholesale > 0 ? round2(100 * (1 - unit / wholesale)) : 0;
  } else {
    const disc = Math.max(0, Math.min(90, Number(ln.discount_pct || 0)));
    unit = round2(wholesale * (1 - disc / 100));
    effDisc = disc;
  }
  return { unit, line_total: round2(unit * qty), effDisc, qty };
};

/**
 * Quote editor. If `quoteId` is set the dialog loads that quote on open;
 * otherwise it starts blank. Every save re-fetches so Priscila sees the
 * computed numbers immediately. Secondary actions (PDF, Excel, Email,
 * Convert) always save first — never download a stale draft.
 */
export default function QuoteEditorDialog({ open, onOpenChange, quoteId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(quoteId || null);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [status, setStatus] = useState('draft');
  const [title, setTitle] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [extraDiscountPct, setExtraDiscountPct] = useState(0);
  const [expiresAt, setExpiresAt] = useState('');
  const [lines, setLines] = useState([]);
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [brandFilter, setBrandFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [productTotal, setProductTotal] = useState(0);
  const [addAllOpen, setAddAllOpen] = useState(false);
  const [addAllBusy, setAddAllBusy] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogLoadingFor, setCatalogLoadingFor] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [convertUserId, setConvertUserId] = useState('');
  const [convertQuery, setConvertQuery] = useState('');

  // Latest-save ref so stale downloads don't open with old payloads.
  const latestIdRef = useRef(null);

  const resetAll = () => {
    setId(null); setQuoteNumber(''); setStatus('draft');
    setTitle(''); setClientCompany(''); setClientName(''); setClientEmail('');
    setShippingAddress(''); setNotes(''); setAdminNotes('');
    setExtraDiscountPct(0); setExpiresAt('');
    setLines([]); setProductQuery(''); setProductResults([]);
    setBulkOpen(false); setBulkText('');
    setAddAllOpen(false); setSelectedSkus([]);
    setCatalogPickerOpen(false); setCatalogQuery('');
    setEmailOpen(false); setEmailTo(''); setEmailMsg('');
    setConvertOpen(false); setConvertUserId(''); setConvertQuery('');
  };

  // Load quote (edit) or clear (new) each time the dialog opens
  useEffect(() => {
    if (!open) return;
    resetAll();
    (async () => {
      api.get('/catalogs', { params: { limit: 200 } })
        .then((r) => setCatalogs(r.data?.items || r.data || []))
        .catch(() => {});
      if (quoteId) {
        setLoading(true);
        try {
          const { data } = await api.get(`/admin/quotes-v2/${quoteId}`);
          hydrate(data);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Could not load quote');
          onOpenChange(false);
        } finally { setLoading(false); }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteId]);

  const hydrate = (data) => {
    setId(data.id);
    latestIdRef.current = data.id;
    setQuoteNumber(data.quote_number || '');
    setStatus(data.status || 'draft');
    setTitle(data.title || '');
    setClientCompany(data.client_company || '');
    setClientName(data.client_name || '');
    setClientEmail(data.client_email || '');
    setShippingAddress(data.shipping_address || '');
    setNotes(data.notes || '');
    setAdminNotes(data.admin_notes || '');
    setExtraDiscountPct(Number(data.extra_discount_pct || 0));
    setExpiresAt((data.expires_at || '').slice(0, 10));
    setLines((data.items || []).map((it) => {
      // Legacy items (no override_price key) round-trip via the saved unit price.
      const legacy = !('override_price' in it);
      const hasOverride = legacy || (it.override_price != null && Number(it.override_price) > 0);
      return {
      sku: it.sku,
      name: it.name,
      brand: it.brand,
      images: it.images || [],
      wholesale_price: Number(it.unit_price || 0),
      msrp: Number(it.msrp || 0),
      qty: it.qty || 1,
      discount_pct: hasOverride ? 0 : Number(it.discount_pct || 0),
      override_price: hasOverride ? Number(legacy ? it.your_unit_price || 0 : it.override_price) : '',
      line_note: it.line_note || '',
      missing: it.missing || false,
      };
    }));
    if (data.client_email) setEmailTo(data.client_email);
  };

  // Keep the browser list hydrated at all times so Priscila can browse
  // without typing anything. Debounced on query/brand/supplier changes.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const params = { limit: 30 };
        if (productQuery.trim()) params.q = productQuery.trim();
        if (brandFilter) params.brand = brandFilter;
        if (supplierFilter) params.supplier = supplierFilter;
        const { data } = await api.get('/products', { params });
        setProductResults(data.items || []);
        setProductTotal(data.total ?? (data.items || []).length);
        setAddAllOpen(false);
        if (data.brands && !brands.length) setBrands(data.brands);
      } catch { setProductResults([]); } finally { setSearching(false); }
    }, productQuery.trim() ? 250 : 0);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuery, brandFilter, supplierFilter, open]);

  // Suppliers list is small and admin-only — load once per dialog open.
  useEffect(() => {
    if (!open || suppliers.length) return;
    api.get('/admin/suppliers')
      .then((r) => setSuppliers(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addProduct = (p) => {
    setLines((cur) => {
      const existing = cur.find((l) => l.sku === p.sku);
      if (existing) {
        return cur.map((l) => l.sku === p.sku ? { ...l, qty: Math.max(1, (parseInt(l.qty, 10) || 1) + 1) } : l);
      }
      return [...cur, emptyLine(p)];
    });
  };

  const updateLine = (sku, patch) => {
    setLines((cur) => cur.map((l) => l.sku === sku ? { ...l, ...patch } : l));
  };

  const removeLine = (sku) => {
    setLines((cur) => cur.filter((l) => l.sku !== sku));
    setSelectedSkus((cur) => cur.filter((s) => s !== sku));
  };

  const toggleSku = (sku) => setSelectedSkus((cur) => (cur.includes(sku) ? cur.filter((s) => s !== sku) : [...cur, sku]));
  const allLinesSelected = lines.length > 0 && selectedSkus.length === lines.length;
  const toggleAllLines = () => setSelectedSkus(allLinesSelected ? [] : lines.map((l) => l.sku));

  const applyBulkDiscount = (pct, scope) => {
    const target = scope === 'all' ? null : new Set(selectedSkus);
    const n = target ? target.size : lines.length;
    setLines((cur) => cur.map((l) => (!target || target.has(l.sku) ? { ...l, discount_pct: pct, override_price: '' } : l)));
    toast.success(`${pct}% off applied to ${n} item${n === 1 ? '' : 's'}`);
  };

  const addAllFiltered = async (qty) => {
    setAddAllBusy(true);
    try {
      const params = { limit: 2000 };
      if (productQuery.trim()) params.q = productQuery.trim();
      if (brandFilter) params.brand = brandFilter;
      if (supplierFilter) params.supplier = supplierFilter;
      const { data } = await api.get('/products', { params });
      const existing = new Set(lines.map((l) => l.sku));
      const additions = (data.items || [])
        .filter((p) => !existing.has(p.sku))
        .map((p) => ({ ...emptyLine(p), qty }));
      if (additions.length) setLines((cur) => [...cur, ...additions]);
      const skipped = (data.items || []).length - additions.length;
      toast[additions.length ? 'success' : 'info'](
        `${additions.length} added${skipped ? ` · ${skipped} already in quote` : ''}`,
      );
      setAddAllOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add all failed');
    } finally { setAddAllBusy(false); }
  };

  const bulkAdd = async () => {
    const raw = bulkText.split(/[\n,\t;]+/).map((s) => s.trim()).filter(Boolean);
    if (!raw.length) return;
    try {
      const { data } = await api.post('/products/lookup', { skus: raw });
      const existing = new Set(lines.map((l) => l.sku));
      const additions = (data.found || [])
        .filter((p) => !existing.has(p.sku))
        .map((p) => emptyLine(p));
      if (additions.length) setLines((cur) => [...cur, ...additions]);
      const missing = (data.missing || []).length;
      const bits = [];
      if (additions.length) bits.push(`${additions.length} added`);
      if (missing) bits.push(`${missing} not found`);
      toast[additions.length ? 'success' : 'info'](bits.join(' · ') || 'Nothing to add');
      setBulkText(''); setBulkOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk add failed');
    }
  };

  const loadCatalog = async (c) => {
    setCatalogLoadingFor(c.id);
    try {
      const { data } = await api.get(`/catalogs/${c.slug}`);
      const skus = (data.products || []).map((p) => p.sku).filter(Boolean);
      if (!skus.length) { toast.info('This catalog has no items'); return; }
      const { data: hydrated } = await api.post('/products/lookup', { skus });
      const existing = new Set(lines.map((l) => l.sku));
      const additions = (hydrated.found || [])
        .filter((p) => !existing.has(p.sku))
        .map((p) => {
          const base = emptyLine(p);
          // Honour catalog-level discount as the per-line starting discount
          if (c.pricing_mode === 'discounted' && c.discount_pct) {
            base.discount_pct = Number(c.discount_pct);
          }
          return base;
        });
      if (additions.length) setLines((cur) => [...cur, ...additions]);
      const missing = (hydrated.missing || []).length;
      const already = skus.length - additions.length - missing;
      const bits = [];
      if (additions.length) bits.push(`${additions.length} added`);
      if (already > 0) bits.push(`${already} already in list`);
      if (missing) bits.push(`${missing} unavailable`);
      toast[additions.length ? 'success' : 'info'](bits.join(' · ') || 'Nothing to add');
      setCatalogPickerOpen(false); setCatalogQuery('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setCatalogLoadingFor(''); }
  };

  const filteredCatalogs = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    if (!q) return catalogs;
    return catalogs.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.slug || '').toLowerCase().includes(q),
    );
  }, [catalogs, catalogQuery]);

  // Live totals
  const totals = useMemo(() => {
    let sub = 0;
    const computed = lines.map((ln) => {
      const r = computeLine(ln);
      sub += r.line_total;
      return r;
    });
    const extra = Math.max(0, Math.min(90, Number(extraDiscountPct || 0)));
    const extraAmt = round2(sub * extra / 100);
    const total = round2(sub - extraAmt);
    return { lineTotals: computed, subtotal: round2(sub), extraAmt, total };
  }, [lines, extraDiscountPct]);

  const buildPayload = () => ({
    title: title.trim(),
    client_name: clientName.trim(),
    client_email: clientEmail.trim() || null,
    client_company: clientCompany.trim(),
    shipping_address: shippingAddress,
    notes,
    admin_notes: adminNotes,
    extra_discount_pct: Number(extraDiscountPct || 0),
    expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    items: lines
      .filter((l) => !l.missing)
      .map((l) => ({
        sku: l.sku,
        qty: Math.max(1, parseInt(l.qty, 10) || 1),
        override_price: l.override_price !== '' && l.override_price != null && Number(l.override_price) > 0
          ? Number(l.override_price) : null,
        discount_pct: Number(l.discount_pct || 0),
        line_note: l.line_note || '',
      })),
  });

  const save = async ({ silent = false } = {}) => {
    setBusy(true);
    try {
      const payload = buildPayload();
      let data;
      if (id) {
        ({ data } = await api.patch(`/admin/quotes-v2/${id}`, payload));
      } else {
        ({ data } = await api.post('/admin/quotes-v2', payload));
      }
      hydrate(data);
      if (!silent) toast.success(`Saved ${data.quote_number}`);
      onSaved?.();
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
      return null;
    } finally { setBusy(false); }
  };

  const openDownload = async (kind) => {
    const data = await save({ silent: true });
    if (!data) return;
    const token = localStorage.getItem('ije_token');
    const url = `${API_BASE}/admin/quotes-v2/${data.id}/${kind}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ijehaus-${data.quote_number}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${kind.toUpperCase()}`);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const sendEmail = async () => {
    if (!emailTo.trim()) return toast.error('Enter a recipient email');
    const data = await save({ silent: true });
    if (!data) return;
    setBusy(true);
    try {
      await api.post(`/admin/quotes-v2/${data.id}/email`, { to: emailTo.trim(), message: emailMsg });
      toast.success(`Quote emailed to ${emailTo.trim()}`);
      setEmailOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email failed');
    } finally { setBusy(false); }
  };

  const openConvert = async () => {
    const data = await save({ silent: true });
    if (!data) return;
    if (!users.length) {
      try {
        const r = await api.get('/admin/users', { params: { status: 'active' } });
        setUsers(r.data || []);
      } catch { /* ignore */ }
    }
    setConvertOpen(true);
  };

  const convert = async () => {
    if (!convertUserId) return toast.error('Pick a customer');
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/quotes-v2/${id}/convert-to-order`, { user_id: convertUserId });
      toast.success(`Draft order ${data.order.order_number} created`);
      setConvertOpen(false);
      onOpenChange(false);
      onSaved?.();
      window.location.href = `/orders/${data.order.id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Convert failed');
    } finally { setBusy(false); }
  };

  const filteredUsers = useMemo(() => {
    const q = convertQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.business_name || '').toLowerCase().includes(q) ||
      (u.contact_name || '').toLowerCase().includes(q),
    );
  }, [users, convertQuery]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-16px)] max-w-5xl max-h-[94vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]"
        data-testid="quote-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">
            {id ? `Quote ${quoteNumber}` : 'New quote'}
          </DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500">
            {id ? (
              <>{status.toUpperCase()} · Edit any line, discount or price</>
            ) : (
              <>Build a priced sheet — assign a customer later</>
            )}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="space-y-6 py-2">
            {/* Client + meta */}
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Title" value={title} onChange={setTitle} testid="quote-title" placeholder="Spring 2026 opener" />
              <TextField label="Expires on" type="date" value={expiresAt} onChange={setExpiresAt} testid="quote-expires" />
              <TextField label="Client / Company" value={clientCompany} onChange={setClientCompany} testid="quote-client-company" placeholder="Acme Co" />
              <TextField label="Client contact" value={clientName} onChange={setClientName} testid="quote-client-name" placeholder="Jane Doe" />
              <TextField label="Client email" type="email" value={clientEmail} onChange={setClientEmail} testid="quote-client-email" placeholder="jane@acme.co" />
              <div>
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Extra discount % (whole quote)</label>
                <div className="relative">
                  <input
                    type="number" min={0} max={90} step={0.5}
                    value={extraDiscountPct}
                    onChange={(e) => setExtraDiscountPct(e.target.value)}
                    className="ije-input h-10 w-full pr-8"
                    data-testid="quote-extra-discount"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-xs">%</span>
                </div>
              </div>
            </div>

            {/* Add products */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500">Add products</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => { setCatalogPickerOpen((v) => !v); setBulkOpen(false); }} className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid="quote-load-catalog-toggle">
                    <BookOpen size={13} /> {catalogPickerOpen ? 'Close' : 'From catalog'}
                  </button>
                  <button type="button" onClick={() => { setBulkOpen((v) => !v); setCatalogPickerOpen(false); }} className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid="quote-bulk-toggle">
                    <ClipboardList size={13} /> {bulkOpen ? 'Close' : 'Bulk paste'}
                  </button>
                </div>
              </div>

              {catalogPickerOpen && (
                <div className="border border-neutral-200 bg-neutral-50 p-3 mb-3">
                  <div className="relative mb-2">
                    {!catalogQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none z-10" />}
                    <input value={catalogQuery} onChange={(e) => setCatalogQuery(e.target.value)} placeholder="Search catalogs…" className="ije-input h-10 w-full" style={{ paddingLeft: catalogQuery ? '1rem' : '2.25rem' }} />
                  </div>
                  {filteredCatalogs.length === 0 ? (
                    <p className="sans text-xs text-neutral-500 py-2">No catalogs match.</p>
                  ) : (
                    <ul className="max-h-56 overflow-y-auto divide-y divide-neutral-100 bg-white border border-neutral-200">
                      {filteredCatalogs.map((c) => {
                        const count = (c.item_skus || []).length;
                        return (
                          <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                            <div className="flex-1 min-w-0">
                              <p className="serif text-sm truncate">{c.name}</p>
                              <p className="sans text-[11px] text-neutral-500 mt-0.5">
                                {count} item{count === 1 ? '' : 's'}
                                {c.pricing_mode === 'discounted' && c.discount_pct ? ` · ${c.discount_pct}% off wholesale` : ''}
                              </p>
                            </div>
                            <button type="button" disabled={catalogLoadingFor === c.id || count === 0} onClick={() => loadCatalog(c)} className="btn-primary h-9 shrink-0 disabled:opacity-40" data-testid={`quote-load-catalog-${c.id}`}>
                              {catalogLoadingFor === c.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                              {catalogLoadingFor === c.id ? 'Loading' : 'Load'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {bulkOpen && (
                <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-2 mb-3">
                  <textarea rows={3} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder="Paste SKUs — one per line or separated by commas." className="ije-input w-full p-3 resize-none text-sm font-mono" />
                  <div className="flex justify-end">
                    <button type="button" onClick={bulkAdd} disabled={!bulkText.trim()} className="btn-primary h-9">Add all</button>
                  </div>
                </div>
              )}

              <div className="relative">
                {!productQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none z-10" />}
                <input value={productQuery} onChange={(e) => setProductQuery(e.target.value)} placeholder="Search products — or browse the full catalog below" className="ije-input h-10 w-full" style={{ paddingLeft: productQuery ? '1rem' : '2.25rem' }} data-testid="quote-product-search" />
                {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-neutral-400" />}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <select
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  className="ije-input h-9 pr-8 flex-1 md:flex-none md:min-w-[140px] text-sm"
                  style={{ padding: '0.4rem 0.6rem' }}
                  data-testid="quote-brand-filter"
                >
                  <option value="">All brands</option>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="ije-input h-9 pr-8 flex-1 md:flex-none md:min-w-[140px] text-sm"
                  style={{ padding: '0.4rem 0.6rem' }}
                  data-testid="quote-supplier-filter"
                >
                  <option value="">All suppliers</option>
                  {suppliers.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.product_count})</option>)}
                </select>
                {(productQuery || brandFilter || supplierFilter) && (
                  <button
                    type="button"
                    onClick={() => { setProductQuery(''); setBrandFilter(''); setSupplierFilter(''); }}
                    className="sans text-[10px] uppercase tracking-widest text-neutral-500 hover:text-black"
                    data-testid="quote-filters-clear"
                  >
                    Clear
                  </button>
                )}
                <span className="sans text-[10px] text-neutral-500 ml-auto">
                  {productResults.length} of {productTotal} shown{productQuery || brandFilter || supplierFilter ? ' (filtered)' : ''}
                </span>
                {(productQuery || brandFilter || supplierFilter) && productTotal > 0 && (
                  <button
                    type="button"
                    onClick={() => setAddAllOpen((v) => !v)}
                    className="btn-outline h-9"
                    data-testid="quote-add-all-filtered"
                  >
                    <Plus size={14} /> Add all {productTotal}
                  </button>
                )}
              </div>
              {addAllOpen && (
                <QuoteAddAllPanel
                  count={productTotal}
                  label={supplierFilter || brandFilter}
                  busy={addAllBusy}
                  onConfirm={addAllFiltered}
                  onCancel={() => setAddAllOpen(false)}
                />
              )}
              {productResults.length > 0 ? (
                <ul className="mt-2 border border-neutral-200 max-h-72 overflow-y-auto divide-y divide-neutral-100" data-testid="quote-product-results">
                  {productResults.map((p) => {
                    const inList = lines.find((l) => l.sku === p.sku);
                    return (
                      <li key={p.sku} className="flex items-center gap-3 px-3 py-2.5">
                        <img src={thumbImg((p.images || [])[0])} alt="" className="w-12 h-12 object-cover border border-neutral-200 flex-shrink-0 bg-white" referrerPolicy="no-referrer" onError={(e) => { e.target.src = FALLBACK_IMG; }} />
                        <div className="flex-1 min-w-0">
                          <p className="sans text-sm text-neutral-900 truncate">{p.name}</p>
                          <p className="sans text-[11px] text-neutral-500 truncate mt-0.5">
                            <span className="uppercase tracking-widest">{p.brand}</span>
                            <span className="mx-1.5">·</span>
                            <span className="font-mono">{p.sku}</span>
                          </p>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className="serif text-sm">{fmtMoney(p.wholesale_price)}</span>
                            {p.msrp ? <span className="sans text-[11px] text-neutral-400 line-through">{fmtMoney(p.msrp)}</span> : null}
                          </div>
                        </div>
                        {inList ? (
                          <span className="inline-flex items-center gap-1 sans text-[10px] tracking-widest uppercase px-2 py-1 border border-neutral-900 bg-neutral-900 text-white shrink-0">
                            <Check size={12} /> {inList.qty} added
                          </span>
                        ) : (
                          <button type="button" onClick={() => addProduct(p)} className="btn-primary h-9 shrink-0" data-testid={`quote-add-product-${p.sku}`}>
                            <Plus size={14} /> Add
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="mt-2 border border-dashed border-neutral-200 py-10 text-center sans text-xs text-neutral-500">
                  {searching ? 'Loading products…' : 'No products match. Try clearing filters.'}
                </div>
              )}
            </div>

            {/* Line items editor */}
            {lines.length > 0 && (
              <div className="border border-neutral-200" data-testid="quote-lines">
                <QuoteBulkDiscountBar
                  total={lines.length}
                  selectedCount={selectedSkus.length}
                  allSelected={allLinesSelected}
                  onToggleAll={toggleAllLines}
                  onApply={applyBulkDiscount}
                />
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-neutral-100">
                  {lines.map((l, idx) => {
                    const { unit, line_total, effDisc } = totals.lineTotals[idx] || {};
                    return (
                      <div key={l.sku} className="p-3" data-testid={`quote-line-${l.sku}`}>
                        <div className="flex items-start gap-2">
                          <input type="checkbox" checked={selectedSkus.includes(l.sku)} onChange={() => toggleSku(l.sku)} className="accent-black w-4 h-4 mt-0.5 shrink-0" data-testid={`quote-line-select-mobile-${l.sku}`} />
                          <div className="flex-1 min-w-0">
                            <p className="sans text-sm text-neutral-900 truncate">{l.name}</p>
                            <p className="sans text-[11px] text-neutral-500 mt-0.5">
                              <span className="uppercase tracking-widest">{l.brand}</span> · <span className="font-mono">{l.sku}</span>
                            </p>
                            <p className="sans text-[11px] text-neutral-500 mt-0.5">Wholesale {fmtMoney(l.wholesale_price)}</p>
                          </div>
                          <button type="button" onClick={() => removeLine(l.sku)} className="p-1.5 text-neutral-400 active:text-red-600 shrink-0"><Trash2 size={16} /></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <LabeledInput label="Qty" type="number" min={1} value={l.qty} onChange={(v) => updateLine(l.sku, { qty: v })} />
                          <LabeledInput label="Disc %" type="number" min={0} max={90} step={0.5} value={l.discount_pct} onChange={(v) => updateLine(l.sku, { discount_pct: v, override_price: '' })} />
                          <LabeledInput label="Override $" type="number" min={0} step={0.01} value={l.override_price} onChange={(v) => updateLine(l.sku, { override_price: v })} placeholder="auto" />
                        </div>
                        <div className="mt-2">
                          <input value={l.line_note} onChange={(e) => updateLine(l.sku, { line_note: e.target.value })} placeholder="Line note (optional)" className="ije-input h-9 w-full text-xs" />
                        </div>
                        <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-neutral-100">
                          <span className="sans text-[10px] tracking-widest uppercase text-neutral-500">Unit {fmtMoney(unit)}{effDisc > 0 ? ` · ${effDisc.toFixed(0)}% off` : ''}</span>
                          <span className="serif text-base">{fmtMoney(line_total)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Desktop table */}
                <table className="hidden md:table w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="w-6 px-2"></th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-left font-medium">Product</th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-right font-medium">Wholesale</th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-right font-medium">Qty</th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-right font-medium">Disc %</th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-right font-medium">Override $</th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-right font-medium">Unit</th>
                      <th className="sans text-[10px] uppercase tracking-widest text-neutral-500 py-2 px-2 text-right font-medium">Line</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, idx) => {
                      const { unit, line_total, effDisc } = totals.lineTotals[idx] || {};
                      return (
                        <tr key={l.sku} className={`border-b border-neutral-100 align-top ${selectedSkus.includes(l.sku) ? 'bg-[color:var(--ije-pink-tint)]/40' : ''}`} data-testid={`quote-line-${l.sku}`}>
                          <td className="py-3 px-2">
                            <input type="checkbox" checked={selectedSkus.includes(l.sku)} onChange={() => toggleSku(l.sku)} className="accent-black w-4 h-4" data-testid={`quote-line-select-${l.sku}`} />
                          </td>
                          <td className="py-2 px-2">
                            <p className="sans text-sm text-neutral-900 truncate max-w-[280px]">{l.name}</p>
                            <p className="sans text-[11px] text-neutral-500 mt-0.5">
                              <span className="uppercase tracking-widest">{l.brand}</span> · <span className="font-mono">{l.sku}</span>
                            </p>
                            <input value={l.line_note} onChange={(e) => updateLine(l.sku, { line_note: e.target.value })} placeholder="Line note…" className="ije-input h-8 w-full text-xs mt-1" />
                          </td>
                          <td className="text-right px-2">{fmtMoney(l.wholesale_price)}</td>
                          <td className="text-right px-2">
                            <input type="number" min="1" value={l.qty} onChange={(e) => updateLine(l.sku, { qty: e.target.value })} className="ije-input h-8 w-16 text-right" data-testid={`quote-qty-${l.sku}`} />
                          </td>
                          <td className="text-right px-2">
                            <input type="number" min="0" max="90" step="0.5" value={l.discount_pct} onChange={(e) => updateLine(l.sku, { discount_pct: e.target.value, override_price: '' })} className="ije-input h-8 w-16 text-right" data-testid={`quote-disc-${l.sku}`} />
                          </td>
                          <td className="text-right px-2">
                            <input type="number" min="0" step="0.01" value={l.override_price} onChange={(e) => updateLine(l.sku, { override_price: e.target.value })} placeholder="auto" className="ije-input h-8 w-20 text-right" data-testid={`quote-override-${l.sku}`} />
                          </td>
                          <td className="text-right px-2">
                            <span className="serif">{fmtMoney(unit)}</span>
                            {effDisc > 0 && <div className="sans text-[10px] text-[color:var(--ije-pink-dark)]">{effDisc.toFixed(0)}% off</div>}
                          </td>
                          <td className="text-right px-2 serif">{fmtMoney(line_total)}</td>
                          <td className="text-right px-2">
                            <button type="button" onClick={() => removeLine(l.sku)} className="text-neutral-400 hover:text-red-600 p-1" title="Remove"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {lines.length === 0 && (
              <div className="border border-dashed border-neutral-200 p-8 text-center sans text-sm text-neutral-500">
                No items yet. Search above, paste SKUs, or load from a catalog.
              </div>
            )}

            {/* Shipping + notes */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Shipping address</label>
                <textarea rows={3} value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} placeholder="Street, City, State ZIP" className="ije-input w-full p-3 resize-none" data-testid="quote-shipping" />
              </div>
              <div>
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Notes (public)</label>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="ije-input w-full p-3 resize-none" data-testid="quote-notes" />
              </div>
            </div>
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">
                Admin notes <span className="normal-case tracking-normal text-neutral-400 ml-1">(private)</span>
              </label>
              <textarea rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="ije-input w-full p-3 resize-none" data-testid="quote-admin-notes" />
            </div>

            {/* Totals */}
            <div className="border border-neutral-200 bg-neutral-50 p-4 flex flex-col items-end gap-1" data-testid="quote-totals">
              <div className="flex items-baseline gap-6">
                <span className="sans text-[10px] tracking-widest uppercase text-neutral-500">Subtotal</span>
                <span className="serif text-lg">{fmtMoney(totals.subtotal)}</span>
              </div>
              {totals.extraAmt > 0 && (
                <div className="flex items-baseline gap-6">
                  <span className="sans text-[10px] tracking-widest uppercase text-[color:var(--ije-pink-dark)]">Extra discount ({Number(extraDiscountPct || 0)}%)</span>
                  <span className="serif text-lg text-[color:var(--ije-pink-dark)]">-{fmtMoney(totals.extraAmt)}</span>
                </div>
              )}
              <div className="flex items-baseline gap-6 pt-1 border-t border-neutral-200 mt-1 w-full justify-end">
                <span className="sans text-xs tracking-widest uppercase">Total</span>
                <span className="serif text-2xl text-[color:var(--ije-pink-dark)]">{fmtMoney(totals.total)}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row sm:flex-wrap gap-2 pt-2 sm:justify-end">
          <button type="button" onClick={() => onOpenChange(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-cancel">
            <X size={14} className="inline mr-1" /> Close
          </button>
          <button type="button" onClick={() => openDownload('xlsx')} disabled={busy || lines.length === 0} className="btn-outline h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-download-xlsx">
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button type="button" onClick={() => openDownload('pdf')} disabled={busy || lines.length === 0} className="btn-outline h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-download-pdf">
            <FileDown size={14} /> PDF
          </button>
          <button type="button" onClick={() => setEmailOpen(true)} disabled={busy || lines.length === 0} className="btn-outline h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-email-open">
            <Mail size={14} /> Email
          </button>
          <button type="button" onClick={openConvert} disabled={busy || lines.length === 0} className="btn-outline h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-convert-open">
            <ArrowRightCircle size={14} /> Convert
          </button>
          <button type="button" onClick={() => save()} disabled={busy} className="btn-primary h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </DialogFooter>

        {/* Email sub-dialog */}
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent className="w-[calc(100vw-16px)] max-w-md p-4 sm:p-6 gap-3 !grid-cols-[minmax(0,1fr)]" data-testid="quote-email-dialog">
            <DialogHeader>
              <DialogTitle className="serif text-lg">Email this quote</DialogTitle>
              <p className="sans text-[10px] tracking-widest uppercase text-neutral-500">PDF is attached automatically</p>
            </DialogHeader>
            <div className="space-y-3">
              <TextField label="Recipient email" type="email" value={emailTo} onChange={setEmailTo} testid="quote-email-to" placeholder="jane@acme.co" />
              <div>
                <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Personal message (optional)</label>
                <textarea rows={3} value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} className="ije-input w-full p-3 resize-none" data-testid="quote-email-msg" placeholder="Hi Jane — attached is the quote we discussed." />
              </div>
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-1">
              <button type="button" onClick={() => setEmailOpen(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto">Cancel</button>
              <button type="button" onClick={sendEmail} disabled={busy || !emailTo.trim()} className="btn-primary h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-email-send">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert sub-dialog */}
        <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
          <DialogContent className="w-[calc(100vw-16px)] max-w-md p-4 sm:p-6 gap-3 !grid-cols-[minmax(0,1fr)]" data-testid="quote-convert-dialog">
            <DialogHeader>
              <DialogTitle className="serif text-lg">Convert to customer order</DialogTitle>
              <p className="sans text-[10px] tracking-widest uppercase text-neutral-500">Creates a Draft order with these prices</p>
            </DialogHeader>
            <div className="space-y-2">
              <div className="relative">
                {!convertQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none z-10" />}
                <input value={convertQuery} onChange={(e) => setConvertQuery(e.target.value)} placeholder="Search approved customers…" className="ije-input h-10 w-full" style={{ paddingLeft: convertQuery ? '1rem' : '2.25rem' }} data-testid="quote-convert-search" />
              </div>
              <ul className="border border-neutral-200 max-h-60 overflow-y-auto divide-y divide-neutral-100">
                {filteredUsers.slice(0, 30).map((u) => (
                  <li key={u.id}>
                    <button type="button" onClick={() => setConvertUserId(u.id)} className={`w-full text-left px-3 py-2.5 hover:bg-neutral-50 ${convertUserId === u.id ? 'bg-[color:var(--ije-pink-tint)]/50' : ''}`} data-testid={`quote-convert-user-${u.id}`}>
                      <p className="sans text-sm text-neutral-900">{u.business_name || u.email}</p>
                      <p className="sans text-xs text-neutral-500 truncate">{u.email} · {Number(u.discount_percent || 0)}% off</p>
                    </button>
                  </li>
                ))}
                {filteredUsers.length === 0 && <li className="px-3 py-6 text-center sans text-xs text-neutral-500">No active customers found.</li>}
              </ul>
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-1">
              <button type="button" onClick={() => setConvertOpen(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto">Cancel</button>
              <button type="button" onClick={convert} disabled={busy || !convertUserId} className="btn-primary h-11 sm:h-10 w-full sm:w-auto" data-testid="quote-convert-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightCircle size={14} />} Create draft order
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function TextField({ label, value, onChange, testid, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="ije-input h-10 w-full"
        data-testid={testid}
      />
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder, ...rest }) {
  return (
    <div>
      <label className="sans text-[9px] tracking-widest uppercase text-neutral-500 block mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="ije-input h-9 w-full text-sm"
        {...rest}
      />
    </div>
  );
}
